import { motion } from "framer-motion";
import {
    Compass, DoorOpen, Flame, BedDouble, Droplet, Sofa, Sparkles, ScanLine,
} from "lucide-react";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";

// Real dimensions produced by the Vastu engine (see backend/vastu_router.py
// SCORE_SYSTEM). Landing preview shows the same six dimensions the user will
// receive in their report — an accurate hint of the product, not a placeholder.
const vastuDimensions = [
    { icon: DoorOpen,  label: "Main Entrance" },
    { icon: Flame,     label: "Kitchen" },
    { icon: BedDouble, label: "Master Bedroom" },
    { icon: Droplet,   label: "Toilets" },
    { icon: Sofa,      label: "Living & Balcony" },
    { icon: Sparkles,  label: "Pooja / Energy Zone" },
];

export default function VastuIntro() {
    const { setActiveSection } = useHomeIQ();
    return (
        <section
            id="vastu"
            data-testid="vastu-intro"
            className="relative py-16 md:py-24 px-6 md:px-12 bg-white border-t border-[#0A1F44]/5"
        >
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    {/* Left: Premium intro */}
                    <div className="lg:col-span-6">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0A1F44]/5 border border-[#0A1F44]/10 text-[#0A1F44] text-[11px] font-semibold tracking-[0.2em] uppercase mb-6">
                            <Compass className="w-3.5 h-3.5 text-[#1E6BFF]" />
                            Before you sign
                        </div>
                        <h2
                            data-testid="vastu-heading"
                            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#0A1F44]"
                        >
                            Does this home
                            <br />
                            feel
                            <br />
                            <span className="text-[#1E6BFF]">right?</span>
                        </h2>
                        <p className="mt-7 text-[#0A1F44]/65 text-base sm:text-lg leading-relaxed max-w-md">
                            When the numbers say yes and the neighbourhood feels
                            right, bring us the home you're closest to choosing.
                            A quiet Vastu read — tradition-aware, never preachy
                            — for the final reassurance before you sign.
                        </p>
                        <button
                            type="button"
                            onClick={() => setActiveSection("vastu")}
                            data-testid="vastu-cta"
                            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#0A1F44] underline underline-offset-4 decoration-[#0A1F44]/20 hover:decoration-[#1E6BFF] hover:text-[#1E6BFF] transition-colors"
                        >
                            Upload my floor plan
                            <span>→</span>
                        </button>
                    </div>

                    {/* Right: Vastu compass visual */}
                    <div className="lg:col-span-6">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{
                                duration: 1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="relative aspect-square max-w-[520px] mx-auto rounded-3xl bg-[#0A1F44] overflow-hidden border border-[#0A1F44]"
                        >
                            <div className="absolute inset-0 blueprint-grid opacity-20" />
                            {/* Compass rings */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="absolute w-[88%] aspect-square rounded-full border border-white/15" />
                                <div className="absolute w-[64%] aspect-square rounded-full border border-white/12" />
                                <div className="absolute w-[40%] aspect-square rounded-full border border-white/10" />
                                {/* Cross axes */}
                                <div className="absolute w-[88%] h-px bg-white/15" />
                                <div className="absolute h-[88%] w-px bg-white/15" />
                                {/* Diagonals */}
                                <div className="absolute w-[88%] h-px bg-white/8 rotate-45" />
                                <div className="absolute w-[88%] h-px bg-white/8 -rotate-45" />

                                {/* Direction labels */}
                                <DirLabel className="top-4 left-1/2 -translate-x-1/2">
                                    N
                                </DirLabel>
                                <DirLabel className="bottom-4 left-1/2 -translate-x-1/2">
                                    S
                                </DirLabel>
                                <DirLabel className="left-4 top-1/2 -translate-y-1/2">
                                    W
                                </DirLabel>
                                <DirLabel className="right-4 top-1/2 -translate-y-1/2">
                                    E
                                </DirLabel>

                                {/* Center: anticipation, not a fake score.
                                    Communicates capability without pretending
                                    we've already scored the user's home. */}
                                <div className="relative z-10 text-center px-3 -translate-y-6 sm:-translate-y-8">
                                    <ScanLine
                                        className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-2 sm:mb-3 text-[#7FA8FF]"
                                        strokeWidth={1.75}
                                    />
                                    <div className="text-[9px] sm:text-[10px] tracking-[0.28em] sm:tracking-[0.3em] uppercase text-[#7FA8FF] font-semibold mb-1.5 sm:mb-2">
                                        AI-Powered
                                    </div>
                                    <div className="font-display font-extrabold text-xl sm:text-3xl md:text-4xl text-white leading-tight">
                                        Vastu
                                        <br />
                                        Intelligence
                                    </div>
                                    <div className="mt-2 sm:mt-3 text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.22em] uppercase text-white/55 font-semibold">
                                        Upload a floor plan to begin
                                    </div>                                </div>
                            </div>

                            {/* Bottom: preview the six real dimensions the
                                Vastu engine actually scores — same names,
                                same order as backend/vastu_router.py. */}
                            <div className="absolute left-0 right-0 bottom-0 p-3 sm:p-4 grid grid-cols-3 gap-1 sm:gap-1.5 text-[9px] tracking-[0.16em] uppercase font-semibold">
                                {vastuDimensions.map((d) => {
                                    const Icon = d.icon;
                                    return (
                                        <div
                                            key={d.label}
                                            data-testid={`vastu-dimension-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                            className="bg-white/5 border border-white/10 rounded-md sm:rounded-lg p-1.5 sm:p-2.5 backdrop-blur-sm flex items-center gap-1.5 sm:gap-2 min-w-0"
                                        >
                                            <Icon
                                                className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#7FA8FF] shrink-0"
                                                strokeWidth={2}
                                            />
                                            <div className="text-white leading-tight normal-case tracking-normal text-[9px] sm:text-[10px] font-medium truncate">
                                                {d.label}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function DirLabel({ children, className = "" }) {
    return (
        <span
            className={`absolute text-white/55 text-[10px] tracking-[0.3em] uppercase font-semibold ${className}`}
        >
            {children}
        </span>
    );
}
